import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_API_URL || "";

// CS2 map radar metadata — values from official game files (resource/overviews/*.txt)
// pos_x/pos_y = top-left world coordinate of the radar image
// scale = world units per radar image pixel
// The radar image is 1024x1024 pixels
// Formula: pixel_x = (world_x - pos_x) / scale
//          pixel_y = (pos_y - world_y) / scale  (Y axis is flipped)
const MAP_DATA = {
  de_dust2:    { pos_x: -2476, pos_y:  3239, scale: 4.4 },
  de_mirage:   { pos_x: -3230, pos_y:  1713, scale: 5.0 },
  de_inferno:  { pos_x: -2087, pos_y:  3870, scale: 4.9 },
  de_nuke:     { pos_x: -3453, pos_y:  2887, scale: 7.0 },
  de_anubis:   { pos_x: -2796, pos_y:  3328, scale: 5.22},
  de_ancient:  { pos_x: -2953, pos_y:  2164, scale: 5.0 },
  de_vertigo:  { pos_x: -3168, pos_y:  1762, scale: 4.0 },
  de_overpass: { pos_x: -4831, pos_y:  1781, scale: 5.2 },
};

// Radar image is 1024x1024. We draw it stretched to CANVAS_SIZE x CANVAS_SIZE.
const RADAR_SIZE = 1024;

// Map images are self-hosted — downloaded from GitHub during Docker build
// and served by Nginx from /maps/<mapname>.png
const KNOWN_MAPS = [
  "de_dust2", "de_mirage", "de_inferno", "de_nuke",
  "de_anubis", "de_ancient", "de_vertigo", "de_overpass"
];

const CANVAS_SIZE = 800;

/**
 * Convert CS2 world coordinates to canvas pixel position
 * The radar image is 1024x1024, stretched to CANVAS_SIZE on screen
 */
function worldToCanvas(worldX, worldY, mapName) {
  const meta = MAP_DATA[mapName];
  if (!meta) {
    // Fallback: simple normalisation
    const nx = (worldX + 3000) / 6000;
    const ny = 1 - (worldY + 3000) / 6000;
    return [nx * CANVAS_SIZE, ny * CANVAS_SIZE];
  }
  // Convert world → radar pixel (0..1024)
  const radarPx = (worldX - meta.pos_x) / meta.scale;
  const radarPy = (meta.pos_y - worldY) / meta.scale;
  // Scale radar pixel → canvas pixel
  const canvasX = (radarPx / RADAR_SIZE) * CANVAS_SIZE;
  const canvasY = (radarPy / RADAR_SIZE) * CANVAS_SIZE;
  return [canvasX, canvasY];
}

function loadImage(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

async function loadMapImage(mapName) {
  if (!mapName) return null;
  if (!KNOWN_MAPS.includes(mapName)) return null;
  // Images are self-hosted in /maps/ by Nginx
  return loadImage(`/maps/${mapName}.png`);
}

const WEAPONS = ["all", "ak47", "m4a1", "awp", "pistol", "knife", "grenade"];
const TYPES = ["kills", "deaths"];

export default function Heatmap() {
  const { matchId } = useParams();
  const canvasRef = useRef(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapImg, setMapImg] = useState(null);
  const [mapImgLoading, setMapImgLoading] = useState(false);
  const [weapon, setWeapon] = useState("all");
  const [type, setType] = useState("kills");
  const [headshotOnly, setHeadshotOnly] = useState(false);
  const [players, setPlayers] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState("all");
  const [tooltip, setTooltip] = useState(null);

  const [selectedMap, setSelectedMap] = useState("de_dust2");
  const [availableMaps, setAvailableMaps] = useState([]);

  // Display toggles
  const [showLines, setShowLines] = useState(false);
  const [showGrenades, setShowGrenades] = useState(false);
  const [grenadeType, setGrenadeType] = useState("all");

  // Zoom & pan state
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const panStart = useRef({ x: 0, y: 0 });

  // Load list of maps that have match data
  useEffect(() => {
    if (matchId) return; // not needed for per-match view
    axios.get(`${API}/api/matches?limit=200`).then(r => {
      const maps = [...new Set((r.data.matches || []).map(m => m.map).filter(Boolean))].sort();
      setAvailableMaps(maps);
      if (maps.length > 0 && !maps.includes(selectedMap)) {
        setSelectedMap(maps[0]);
      }
    }).catch(console.error);
  }, [matchId]);

  // Load heatmap data
  useEffect(() => {
    setLoading(true);
    const url = matchId
      ? `${API}/api/heatmap/${matchId}?type=${type}`
      : `${API}/api/heatmap/map/${selectedMap}?type=${type}`;
    axios.get(url).then(r => {
      setData(r.data);
      const playerMap = {};
      for (const p of (r.data.points || [])) {
        if (p.attacker_name && !playerMap[p.attacker_name]) {
          playerMap[p.attacker_name] = p.attacker_name;
        }
      }
      setPlayers(Object.keys(playerMap));
    }).catch(console.error).finally(() => setLoading(false));
  }, [matchId, type, selectedMap]);

  // Load map image when map name becomes known
  useEffect(() => {
    if (!data?.map) return;
    setMapImgLoading(true);
    setMapImg(null);
    loadMapImage(data.map).then(img => {
      setMapImg(img);
      setMapImgLoading(false);
    });
  }, [data?.map]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !data) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width, H = canvas.height;

    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = "#0a0c12";
    ctx.fillRect(0, 0, W, H);

    // Apply zoom/pan transform
    ctx.save();
    ctx.translate(pan.x, pan.y);
    ctx.scale(zoom, zoom);

    // Draw map overview image
    if (mapImg) {
      ctx.globalAlpha = 0.55;
      ctx.drawImage(mapImg, 0, 0, W, H);
      ctx.globalAlpha = 1.0;
    } else {
      ctx.strokeStyle = "rgba(255,255,255,0.04)";
      ctx.lineWidth = 1;
      for (let i = 0; i <= 10; i++) {
        const x = (W / 10) * i, y = (H / 10) * i;
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
      }
    }

    const pistols = ["glock", "usp_silencer", "p250", "deagle", "fiveseven", "tec9", "cz75a", "revolver", "elite", "p2000"];
    const grenades = ["hegrenade", "flashbang", "smokegrenade", "molotov", "incgrenade", "decoy"];

    let points = data.points || [];
    if (headshotOnly) points = points.filter(p => p.headshot);
    if (selectedPlayer !== "all") {
      points = points.filter(p =>
        type === "kills" ? p.attacker_name === selectedPlayer : p.victim_name === selectedPlayer
      );
    }
    if (weapon !== "all") {
      if (weapon === "pistol") points = points.filter(p => pistols.includes(p.weapon));
      else if (weapon === "grenade") points = points.filter(p => grenades.includes(p.weapon));
      else points = points.filter(p => p.weapon?.includes(weapon));
    }

    // Dot size scales down as you zoom in so they don't overwhelm
    const dotSize = Math.max(2, 4 / zoom);
    const glowSize = Math.max(6, 16 / zoom);

    // ── Grenades ─────────────────────────────────────────────────────────
    if (showGrenades && data.grenades?.length > 0) {
      const GRENADE_COLORS = {
        CSmokeGrenade:      "#60a5fa",
        CFlashbang:         "#fef08a",
        CHEGrenade:         "#f87171",
        CMolotovGrenade:    "#fb923c",
        CIncendiaryGrenade: "#fb923c",
        CDecoyGrenade:      "#a78bfa",
      };
      let grenades = data.grenades || [];
      if (grenadeType !== "all") {
        grenades = grenades.filter(g => g.grenade_type === grenadeType);
      }
      for (const g of grenades) {
        if (g.x == null || g.y == null) continue;
        const [gx, gy] = worldToCanvas(g.x, g.y, data.map);
        const color = GRENADE_COLORS[g.grenade_type] || "#e5e7eb";
        const r = Math.max(3, 5 / zoom);

        // Glow
        const grd = ctx.createRadialGradient(gx, gy, 0, gx, gy, r * 3);
        grd.addColorStop(0, color.replace(")", ", 0.5)").replace("rgb", "rgba"));
        grd.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = grd;
        ctx.beginPath();
        ctx.arc(gx, gy, r * 3, 0, Math.PI * 2);
        ctx.fill();

        // Diamond shape for grenades
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.moveTo(gx, gy - r);
        ctx.lineTo(gx + r, gy);
        ctx.lineTo(gx, gy + r);
        ctx.lineTo(gx - r, gy);
        ctx.closePath();
        ctx.fill();
      }
    }

    // ── Kill lines (attacker → victim) ───────────────────────────────────
    if (showLines) {
      ctx.lineWidth = Math.max(0.5, 1 / zoom);
      for (const pt of points) {
        if (pt.x == null || pt.y == null || pt.x2 == null || pt.y2 == null) continue;
        const [ax, ay] = worldToCanvas(pt.x, pt.y, data.map);
        const [vx, vy] = worldToCanvas(pt.x2, pt.y2, data.map);
        ctx.strokeStyle = pt.headshot ? "rgba(255,60,60,0.35)" : "rgba(240,165,0,0.25)";
        ctx.beginPath();
        ctx.moveTo(ax, ay);
        ctx.lineTo(vx, vy);
        ctx.stroke();
      }
    }

    // ── Kill dots ─────────────────────────────────────────────────────────
    for (const pt of points) {
      if (pt.x == null || pt.y == null) continue;
      const [cx, cy] = worldToCanvas(pt.x, pt.y, data.map);

      const grd = ctx.createRadialGradient(cx, cy, 0, cx, cy, glowSize);
      if (type === "kills") {
        grd.addColorStop(0, pt.headshot ? "rgba(255,60,60,0.75)" : "rgba(240,165,0,0.6)");
        grd.addColorStop(1, "rgba(0,0,0,0)");
      } else {
        grd.addColorStop(0, "rgba(60,140,255,0.7)");
        grd.addColorStop(1, "rgba(0,0,0,0)");
      }
      ctx.fillStyle = grd;
      ctx.beginPath();
      ctx.arc(cx, cy, glowSize, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = pt.headshot
        ? (type === "kills" ? "#ff3c3c" : "#60a5fa")
        : (type === "kills" ? "#f0a500" : "#3b82f6");
      ctx.beginPath();
      ctx.arc(cx, cy, pt.headshot ? dotSize * 1.3 : dotSize, 0, Math.PI * 2);
      ctx.fill();

      // Draw victim dot at other end when showing lines
      if (showLines && pt.x2 != null) {
        const [vx, vy] = worldToCanvas(pt.x2, pt.y2, data.map);
        ctx.fillStyle = "rgba(255,255,255,0.4)";
        ctx.beginPath();
        ctx.arc(vx, vy, dotSize * 0.8, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    ctx.restore();

    // Stats overlay — drawn outside transform so it stays fixed
    ctx.font = "bold 12px monospace";
    ctx.fillStyle = "rgba(0,0,0,0.6)";
    ctx.fillRect(0, H - 28, W, 28);
    ctx.fillStyle = "rgba(255,255,255,0.5)";
    ctx.fillText(
      `${points.length} events  |  ${data.map || ""}  |  zoom: ${zoom.toFixed(1)}x`,
      10, H - 10
    );
    if (!mapImg && !mapImgLoading) {
      ctx.fillStyle = "rgba(255,200,0,0.6)";
      ctx.fillText("Map image unavailable", W / 2 - 70, H - 10);
    }
  }, [data, mapImg, weapon, type, headshotOnly, selectedPlayer, mapImgLoading, zoom, pan, showLines, showGrenades, grenadeType]);

  useEffect(() => { draw(); }, [draw]);

  const handleCanvasHover = (e) => {
    const canvas = canvasRef.current;
    if (!canvas || !data) return;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    // Convert screen coords → canvas coords → world coords (accounting for zoom/pan)
    const rawX = (e.clientX - rect.left) * scaleX;
    const rawY = (e.clientY - rect.top) * scaleY;
    const mx = (rawX - pan.x) / zoom;
    const my = (rawY - pan.y) / zoom;

    const hitRadius = 20 / zoom;
    let nearest = null, minDist = hitRadius;
    for (const pt of (data.points || [])) {
      if (pt.x == null) continue;
      const [cx, cy] = worldToCanvas(pt.x, pt.y, data.map);
      const d = Math.hypot(cx - mx, cy - my);
      if (d < minDist) { minDist = d; nearest = pt; }
    }
    setTooltip(nearest ? { ...nearest, screenX: e.clientX, screenY: e.clientY } : null);
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const mouseX = (e.clientX - rect.left) * scaleX;
    const mouseY = (e.clientY - rect.top) * scaleY;

    const zoomFactor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
    const newZoom = Math.min(Math.max(zoom * zoomFactor, 1), 8);

    // Zoom towards mouse position
    const newPanX = mouseX - (mouseX - pan.x) * (newZoom / zoom);
    const newPanY = mouseY - (mouseY - pan.y) * (newZoom / zoom);

    // Clamp pan so map doesn't go out of bounds
    const W = canvas.width;
    const H = canvas.height;
    setZoom(newZoom);
    setPan({
      x: Math.min(0, Math.max(newPanX, W - W * newZoom)),
      y: Math.min(0, Math.max(newPanY, H - H * newZoom)),
    });
  };

  const handleMouseDown = (e) => {
    if (zoom <= 1) return;
    isDragging.current = true;
    dragStart.current = { x: e.clientX, y: e.clientY };
    panStart.current = { ...pan };
  };

  const handleMouseMove = (e) => {
    handleCanvasHover(e);
    if (!isDragging.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const W = canvas.width, H = canvas.height;
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    const scaleX = canvas.width / canvas.getBoundingClientRect().width;
    const scaleY = canvas.height / canvas.getBoundingClientRect().height;
    const newX = panStart.current.x + dx * scaleX;
    const newY = panStart.current.y + dy * scaleY;
    setPan({
      x: Math.min(0, Math.max(newX, W - W * zoom)),
      y: Math.min(0, Math.max(newY, H - H * zoom)),
    });
  };

  const handleMouseUp = () => { isDragging.current = false; };

  const resetZoom = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  const btnStyle = (active) => ({
    padding: "0.3rem 0.8rem", borderRadius: "5px", border: "none",
    cursor: "pointer", fontSize: "0.8rem",
    background: active ? "#f0a500" : "#2d3148",
    color: active ? "#0f1117" : "#e5e7eb",
    fontWeight: active ? "700" : "400",
  });

  return (
    <div>
      <div style={{ marginBottom: "1rem" }}>
        {matchId && (
          <Link to={`/matches/${matchId}`} style={{ color: "#9ca3af", fontSize: "0.9rem" }}>
            ← Back to Match
          </Link>
        )}
      </div>

      <h1>🗺️ Kill Heatmap {data?.map ? `— ${data.map}` : ""}</h1>

      <div className="card" style={{ marginBottom: "1rem" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem", alignItems: "center" }}>
          {/* Map selector — only on global heatmap page */}
          {!matchId && availableMaps.length > 0 && (
            <select
              value={selectedMap}
              onChange={e => setSelectedMap(e.target.value)}
              style={{ background: "#2d3148", color: "#e5e7eb", border: "none", borderRadius: "5px", padding: "0.35rem 0.8rem", fontSize: "0.85rem", fontWeight: "600" }}
            >
              {availableMaps.map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          )}
          <div style={{ display: "flex", gap: "0.3rem" }}>
            {TYPES.map(t => (
              <button key={t} style={btnStyle(type === t)} onClick={() => setType(t)}>
                {t === "kills" ? "💀 Kills" : "☠️ Deaths"}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", gap: "0.3rem", flexWrap: "wrap" }}>
            {WEAPONS.map(w => (
              <button key={w} style={btnStyle(weapon === w)} onClick={() => setWeapon(w)}>{w}</button>
            ))}
          </div>
          <button style={btnStyle(headshotOnly)} onClick={() => setHeadshotOnly(v => !v)}>
            🎯 HS Only
          </button>
          <button style={btnStyle(showLines)} onClick={() => setShowLines(v => !v)}
            title="Draw a line from attacker to victim for each kill">
            ↗ Kill Lines
          </button>
          <button style={btnStyle(showGrenades)} onClick={() => setShowGrenades(v => !v)}
            title="Show grenade landing positions">
            💣 Grenades
          </button>
          {showGrenades && (
            <select value={grenadeType} onChange={e => setGrenadeType(e.target.value)}
              style={{ background: "#2d3148", color: "#e5e7eb", border: "none", borderRadius: "5px", padding: "0.3rem 0.6rem", fontSize: "0.8rem" }}>
              <option value="all">All grenades</option>
              <option value="CSmokeGrenade">Smoke</option>
              <option value="CFlashbang">Flash</option>
              <option value="CHEGrenade">HE</option>
              <option value="CMolotovGrenade">Molotov</option>
              <option value="CIncendiaryGrenade">Incendiary</option>
              <option value="CDecoyGrenade">Decoy</option>
            </select>
          )}
          {players.length > 0 && (
            <select value={selectedPlayer} onChange={e => setSelectedPlayer(e.target.value)}
              style={{ background: "#2d3148", color: "#e5e7eb", border: "none", borderRadius: "5px", padding: "0.3rem 0.6rem", fontSize: "0.8rem" }}>
              <option value="all">All Players</option>
              {players.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          )}
        </div>
        <div style={{ display: "flex", gap: "1.5rem", marginTop: "0.75rem", fontSize: "0.78rem", color: "#9ca3af" }}>
          <span><span style={{ color: "#ff3c3c" }}>●</span> Headshot kill</span>
          <span><span style={{ color: "#f0a500" }}>●</span> Kill</span>
          <span><span style={{ color: "#3b82f6" }}>●</span> Death</span>
          {showGrenades && <>
            <span><span style={{ color: "#60a5fa" }}>◆</span> Smoke</span>
            <span><span style={{ color: "#fef08a" }}>◆</span> Flash</span>
            <span><span style={{ color: "#fb923c" }}>◆</span> Molotov</span>
            <span><span style={{ color: "#f87171" }}>◆</span> HE</span>
          </>}
          {mapImgLoading && <span style={{ color: "#6b7280" }}>Loading map image...</span>}
          {!mapImg && !mapImgLoading && data?.map && <span style={{ color: "#f59e0b" }}>⚠ Map image not available</span>}
          {mapImg && <span style={{ color: "#4ade80" }}>✓ Map overlay loaded</span>}
        </div>
      </div>

      {loading ? (
        <div className="card" style={{ textAlign: "center", padding: "3rem", color: "#9ca3af" }}>
          Loading heatmap data...
        </div>
      ) : (
        <div style={{ position: "relative" }}>
          {/* Zoom controls */}
          <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.5rem", alignItems: "center" }}>
            <button onClick={() => handleWheel({ deltaY: -1, clientX: CANVAS_SIZE/2, clientY: CANVAS_SIZE/2, preventDefault: ()=>{} })}
              style={{ background: "#2d3148", color: "#e5e7eb", border: "none", borderRadius: "5px", padding: "0.3rem 0.8rem", cursor: "pointer", fontSize: "1rem" }}>
              +
            </button>
            <button onClick={() => handleWheel({ deltaY: 1, clientX: CANVAS_SIZE/2, clientY: CANVAS_SIZE/2, preventDefault: ()=>{} })}
              style={{ background: "#2d3148", color: "#e5e7eb", border: "none", borderRadius: "5px", padding: "0.3rem 0.8rem", cursor: "pointer", fontSize: "1rem" }}>
              −
            </button>
            <span style={{ color: "#9ca3af", fontSize: "0.8rem" }}>{zoom.toFixed(1)}x</span>
            {zoom > 1 && (
              <button onClick={resetZoom}
                style={{ background: "#2d3148", color: "#e5e7eb", border: "none", borderRadius: "5px", padding: "0.3rem 0.8rem", cursor: "pointer", fontSize: "0.8rem" }}>
                Reset
              </button>
            )}
            <span style={{ color: "#6b7280", fontSize: "0.75rem" }}>Scroll to zoom · Drag to pan</span>
          </div>
          <canvas
            ref={canvasRef}
            width={CANVAS_SIZE}
            height={CANVAS_SIZE}
            style={{
              width: "100%", maxWidth: `${CANVAS_SIZE}px`, display: "block",
              borderRadius: "10px", border: "1px solid #2d3148",
              cursor: isDragging.current ? "grabbing" : zoom > 1 ? "grab" : "crosshair"
            }}
            onMouseMove={handleMouseMove}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseLeave={() => { setTooltip(null); isDragging.current = false; }}
            onWheel={handleWheel}
          />
          {tooltip && (
            <div style={{
              position: "fixed", left: tooltip.screenX + 14, top: tooltip.screenY - 10,
              background: "#1a1d26", border: "1px solid #2d3148", borderRadius: "6px",
              padding: "0.5rem 0.75rem", fontSize: "0.8rem", pointerEvents: "none", zIndex: 100,
              boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
            }}>
              <div style={{ color: "#f0a500", fontWeight: "600", marginBottom: "2px" }}>{tooltip.weapon}</div>
              {tooltip.attacker_name && <div>By: <strong>{tooltip.attacker_name}</strong></div>}
              {tooltip.victim_name && <div>→ <strong>{tooltip.victim_name}</strong></div>}
              {tooltip.headshot && <div style={{ color: "#ff3c3c" }}>🎯 Headshot</div>}
              <div style={{ color: "#6b7280", marginTop: "2px" }}>Round {tooltip.round_number}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
